import { Component, Input, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { RestProviderService } from '../../../../services/rest-provider/rest-provider.service';

interface ActivityLogEntryDto {
  Id: number;
  EntryType: string;
  Label: string;
  OldValueDisplay: string | null;
  NewValueDisplay: string | null;
  OccurredAt: string;
  ModifiedByUserFullName: string | null;
  BatchId: string;
}

interface ActivityGroup {
  batchId: string;
  entries: ActivityLogEntryDto[];
  expanded: boolean;
}

const ICONS: Record<string, string> = {
  Created: 'add',
  FieldChanged: 'edit',
  StatusChanged: 'sync_alt',
  LocationAdded: 'location_on',
  LocationRemoved: 'location_off',
  ProcedureCreated: 'gavel',
  ProcedureRemoved: 'gavel',
  ProcedureFinalized: 'verified',
  Generic: 'info',
};

@Component({
  selector: 'sem-activity-trail',
  templateUrl: './sem-activity-trail.component.html',
  styleUrls: ['./sem-activity-trail.component.css'],
  standalone: false
})
export class SemActivityTrailComponent implements OnInit, OnDestroy {

  @Input() settings: any;

  private static readonly COLLAPSED_COUNT = 6;

  groups: ActivityGroup[] = [];
  loading = false;
  expanded = false;

  private modelSub: Subscription | null = null;
  private currentDocumentId: number | null = null;

  get visibleGroups(): ActivityGroup[] {
    return this.expanded ? this.groups : this.groups.slice(0, SemActivityTrailComponent.COLLAPSED_COUNT);
  }

  get hiddenCount(): number {
    return Math.max(0, this.groups.length - SemActivityTrailComponent.COLLAPSED_COUNT);
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  toggleGroup(group: ActivityGroup): void {
    group.expanded = !group.expanded;
  }

  constructor(private restProvider: RestProviderService, private elementRef: ElementRef<HTMLElement>) { }

  ngOnInit() {
    this.modelSub = this.restProvider.modelValueChanged.subscribe((model: any) => {
      if (!model || !model.Id || model.Id === this.currentDocumentId) {
        return;
      }
      this.currentDocumentId = model.Id;
      this.load(model.Id);
    });
  }

  ngOnDestroy() {
    this.modelSub?.unsubscribe();

    // wg-panel-manager.directive.ts reparents our ancestor .dynamic-sidebar-container
    // straight to <body> (so its position:fixed resolves against the viewport, not
    // whatever ancestor it originally rendered under - see that file's comments).
    // That breaks Angular's own cleanup on route change: the renderer still thinks
    // the container's parent is wherever it originally rendered in the form's control
    // tree, so its removeChild call targets the wrong parent and silently no-ops,
    // leaving the panel orphaned under <body> - still visible, unstyled, showing
    // stale content, until a manual page refresh. Since this component's ngOnDestroy
    // reliably fires on route change regardless of where the DOM node physically
    // sits, remove the reparented container ourselves as a definitive cleanup.
    this.elementRef.nativeElement.closest('.dynamic-sidebar-container')?.remove();
  }

  get title(): string {
    return this.settings?.Title || 'Dnevnik aktivnosti';
  }

  /**
   * The backend logs every plain field edit (title, dates, status) as the same
   * generic "FieldChanged" type - there's no separate "status changed" entry type.
   * Status transitions are the one thing worth calling out visually on their own
   * (a different marker color from a plain text/date edit), and the field label
   * reliably says "status" ("Status projekta", "Status postopka") because that's
   * literally the translated field name, so a label check is enough here.
   */
  visualType(entry: ActivityLogEntryDto): string {
    if (entry.EntryType === 'FieldChanged' && /status/i.test(entry.Label)) {
      return 'StatusChanged';
    }
    return entry.EntryType;
  }

  iconFor(visualType: string): string {
    return ICONS[visualType] || ICONS['Generic'];
  }

  /** Slovenian plural forms for "N spremembe" (2 = dual, 3-4 = plural, 5+ = genitive plural). */
  changeCountLabel(n: number): string {
    if (n === 2) {
      return `${n} spremembi`;
    }
    if (n === 3 || n === 4) {
      return `${n} spremembe`;
    }
    return `${n} sprememb`;
  }

  private load(documentId: number) {
    const endpoint = (this.settings?.ApiEndpoint || 'api/activitylog').replace(/\/$/, '');
    this.loading = true;
    this.expanded = false;

    this.restProvider.getDataGET(`${endpoint}/${documentId}`).subscribe({
      next: (response: any) => {
        try {
          const entries: ActivityLogEntryDto[] = JSON.parse(response as string) || [];
          this.groups = this.groupByBatch(entries);
        } catch {
          this.groups = [];
        }
        this.loading = false;
      },
      error: () => {
        this.groups = [];
        this.loading = false;
      }
    });
  }

  /**
   * Groups entries written from the same save (shared BatchId) into one row, so a save that
   * touched several fields shows as a single, expandable "N spremembe" entry instead of N
   * separate timeline rows. Entries arrive newest-first from the API; grouping by a map instead
   * of just checking adjacency is a little more defensive but costs nothing.
   */
  private groupByBatch(entries: ActivityLogEntryDto[]): ActivityGroup[] {
    const byBatch = new Map<string, ActivityGroup>();
    const order: string[] = [];

    for (const entry of entries) {
      const key = entry.BatchId || String(entry.Id);
      let group = byBatch.get(key);
      if (!group) {
        group = { batchId: key, entries: [], expanded: false };
        byBatch.set(key, group);
        order.push(key);
      }
      group.entries.push(entry);
    }

    return order.map(key => byBatch.get(key)!);
  }

  formatDate(value: string): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return value;
    }
    const pad = (n: number) => n < 10 ? '0' + n : String(n);
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  }

  formatTime(value: string): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return '';
    }
    const pad = (n: number) => n < 10 ? '0' + n : String(n);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
